import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Loader2, Sparkles } from 'lucide-react';
import { getOptimizedImageUrl } from '../../utils/image';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Define subcategories that should be hidden from main category grid
// These appear nested under their parent category on the Products page
const SUBCATEGORY_HANDLES = [
  'ruby-gemstone',
  'moonga-gemstone',
  'gomeda-gemstone',
  'emerald-gemstone', 
  'blue-sapphire',
  'yellow-sapphire',
  'pearl-gemstone',
  'coral-gemstone',
  'hessonite-gemstone',
  'cats-eye-gemstone',
  'diamond-gemstone',
  // Add more subcategory handles here as needed
];

// Fallback images for collections without images
const fallbackImages = {
  'bracelets': 'https://images.unsplash.com/photo-1611591437281-460bfbe1220a?w=600&q=80',
  'rudraksha': 'https://static.prod-images.emergentagent.com/jobs/d8be5d08-cdbd-415f-8a8e-77a63a782ae0/images/70db6c36cb30fac5305543156f02192937d2536e985d6b2cd5611292568bfc02.png',
  'gemstones': 'https://static.prod-images.emergentagent.com/jobs/d8be5d08-cdbd-415f-8a8e-77a63a782ae0/images/ad3cc640fbaa880d8c7af745926d77d8fdcbc468a0e4d6ab693ad133263703b3.png',
  'astrology': 'https://static.prod-images.emergentagent.com/jobs/d8be5d08-cdbd-415f-8a8e-77a63a782ae0/images/90bee3f5c3940ffb4d4c1bbd06dd7ddfdc5615be0130a4d96d45622d410f3f89.png',
  'yantras': 'https://static.prod-images.emergentagent.com/jobs/d8be5d08-cdbd-415f-8a8e-77a63a782ae0/images/364fbb369f4a79008410a058106add07bddfa157a695c7d498b6859a6430c361.png',
  'malas': 'https://static.prod-images.emergentagent.com/jobs/d8be5d08-cdbd-415f-8a8e-77a63a782ae0/images/14de9ef86baa137ea61fc9cd39310ebdb26a02b9b83ff6a6f506e5a52d771d9e.png',
  'crystal': 'https://images.unsplash.com/photo-1626470408813-f0059745d58b?w=600&q=80',
  'pyramids': 'https://static.prod-images.emergentagent.com/jobs/d8be5d08-cdbd-415f-8a8e-77a63a782ae0/images/83ccc4f8005e3856cdd20c3123dca9403933427510822509ac9aa4b4433eb948.png',
  'murtis': 'https://static.prod-images.emergentagent.com/jobs/d8be5d08-cdbd-415f-8a8e-77a63a782ae0/images/c2cd675190a90b4628c828702d02ed4374d4806156cbdb929618288ec7fb88c9.png',
  'pooja': 'https://static.prod-images.emergentagent.com/jobs/d8be5d08-cdbd-415f-8a8e-77a63a782ae0/images/8cb8d106b202a8f8e2221f3eb34a73a5709ea11d1de58e067dd9b86549793722.png',
  'spray': 'https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?w=600&q=80',
  'paintings': 'https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?w=600&q=80',
  'remedies': 'https://images.unsplash.com/photo-1609710228159-0fa9bd7c0827?w=600&q=80',
  'treatment': 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=600&q=80',
  'pyrite': 'https://images.unsplash.com/photo-1610395219791-21b0353e43cb?w=600&q=80',
  'devta': 'https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?w=600&q=80',
  '3d': 'https://images.unsplash.com/photo-1618005198919-d3d4b5a92ead?w=600&q=80',
  'default': 'https://images.unsplash.com/photo-1609710228159-0fa9bd7c0827?w=600&q=80',
};

const getCategoryImage = (category) => {
  // If Shopify provides an image, use it
  if (category.image) return category.image;
  
  // Otherwise find matching fallback
  const handle = (category.handle || '').toLowerCase();
  const name = (category.name || '').toLowerCase();
  
  for (const [key, url] of Object.entries(fallbackImages)) {
    if (handle.includes(key) || name.includes(key)) {
      return url;
    }
  }
  
  return fallbackImages.default;
};

export const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false); // Start with false

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/categories`);
      const data = await response.json();
      
      // Filter out:
      // 1. "Home page" collection
      // 2. Subcategories (they appear nested on Products page)
      const filtered = data.filter(c => 
        c.name.toLowerCase() !== 'home page' && 
        c.handle !== 'frontpage' &&
        !SUBCATEGORY_HANDLES.includes(c.handle)
      );
      
      setCategories(filtered);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
    setLoading(false);
  };

  // Show skeleton while loading
  if (loading || categories.length === 0) {
    return (
      <section className="py-12 lg:py-16 bg-white" data-testid="categories-section">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="font-serif text-2xl lg:text-3xl font-bold text-foreground">
              Shop by <span className="text-gold">Category</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="bg-gray-100 rounded-xl p-4 animate-pulse">
                <div className="aspect-square bg-gray-200 rounded-lg mb-2"></div>
                <div className="h-4 bg-gray-200 rounded mx-auto w-2/3"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Determine grid columns based on category count
  const getGridClass = () => {
    const count = categories.length;
    if (count <= 4) return 'grid-cols-2 md:grid-cols-4';
    if (count <= 6) return 'grid-cols-2 md:grid-cols-3 lg:grid-cols-6';
    return 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6';
  };

  return (
    <section className="py-12 lg:py-16 bg-gradient-to-b from-white to-amber-50/30" data-testid="categories-section">
      <div className="container mx-auto px-4 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-100 text-amber-700 text-sm font-medium mb-4">
            <Sparkles className="w-4 h-4" />
            Curated Collections
          </div>
          <h2 className="font-serif text-3xl lg:text-4xl font-bold text-foreground mb-3">
            Shop by <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-orange-600">Category</span>
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Explore our carefully curated collections of authentic spiritual products
          </p>
        </motion.div>

        {/* Categories Grid - Dynamic based on count */}
        <div className={`grid gap-4 ${getGridClass()}`}>
          {categories.map((category, index) => {
            const imageUrl = getCategoryImage(category);
            
            return (
              <motion.div
                key={category.handle || index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-20px" }}
                transition={{ delay: Math.min(index * 0.05, 0.3) }}
              >
                <Link
                  to={`/products?category=${category.handle}`}
                  className="group block relative overflow-hidden rounded-xl bg-white shadow-md hover:shadow-xl transition-all duration-300 border border-amber-100 hover:border-amber-400"
                  data-testid={`category-${category.handle}`}
                >
                  <div className="aspect-square overflow-hidden bg-amber-50">
                    <img
                      src={getOptimizedImageUrl(imageUrl, 400)}
                      alt={category.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      loading="lazy"
                      onError={(e) => { e.target.src = fallbackImages.default; }}
                    />
                  </div>
                  
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-white font-semibold text-sm flex items-center gap-1">
                      Explore <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>

                  <div className="p-3 bg-white text-center">
                    <h3 className="font-medium text-foreground text-sm truncate group-hover:text-gold transition-colors">
                      {category.name}
                    </h3>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-10"
        >
          <Link
            to="/products"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold hover:shadow-lg hover:scale-105 transition-all"
          >
            View All Products
            <ArrowRight className="w-5 h-5" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
